import dayjs, { Dayjs } from 'dayjs';

export type TimestampRange = {
  fromTimestamp?: number;
  toTimestamp?: number;
};

export type DateObjectRange = {
  fromDate: Dayjs;
  toDate: Dayjs;
};

export const PREDEFINED_DATE_RANGE_OPTIONS = [
  { id: 'custom', name: 'Custom' },
  { id: 'today', name: 'Today' },
  { id: 'yesterday', name: 'Yesterday' },
  { id: 'this_week', name: 'This Week' },
  { id: 'last_week', name: 'Last Week' },
  { id: 'last_7_days', name: 'Last 7 Days' },
  { id: 'last_30_days', name: 'Last 30 Days' },
  { id: 'this_month', name: 'This Month' },
  { id: 'last_month', name: 'Last Month' },
] as const;

export type DateRangeOption = (typeof PREDEFINED_DATE_RANGE_OPTIONS)[number]['id'];

export const generateDateRange = (dateRangeOption: DateRangeOption): { fromDate: Dayjs; toDate: Dayjs } => {
  const now = dayjs();
  switch (dateRangeOption) {
    case 'today':
      return {
        fromDate: now.startOf('day'),
        toDate: now.endOf('day'),
      };
    case 'yesterday': {
      const yesterday = now.subtract(1, 'day');
      return {
        fromDate: yesterday.startOf('day'),
        toDate: yesterday.endOf('day'),
      };
    }
    case 'this_week':
      return {
        fromDate: now.startOf('week'),
        toDate: now.endOf('week'),
      };
    case 'last_week': {
      const lastWeek = now.subtract(1, 'week');
      return {
        fromDate: lastWeek.startOf('week'),
        toDate: lastWeek.endOf('week'),
      };
    }
    // "Last N days" includes today plus previous N-1 days (N total calendar days)
    case 'last_7_days': {
      return {
        fromDate: now.subtract(6, 'day').startOf('day'),
        toDate: now.endOf('day'),
      };
    }
    // "Last N days" includes today plus previous N-1 days (N total calendar days)
    case 'last_30_days': {
      return {
        fromDate: now.subtract(29, 'day').startOf('day'),
        toDate: now.endOf('day'),
      };
    }
    case 'this_month': {
      return {
        fromDate: now.startOf('month'),
        toDate: now.endOf('month'),
      };
    }
    case 'last_month': {
      const lastMonth = now.subtract(1, 'month');
      return {
        fromDate: lastMonth.startOf('month'),
        toDate: lastMonth.endOf('month'),
      };
    }
    default:
      return {
        fromDate: now.startOf('day'),
        toDate: now.endOf('day'),
      };
  }
};

export const formatDate = (date: Dayjs | string | Date, format = 'ddd DD MMM HH:mm') => {
  return dayjs(date).format(format);
};

/**
 * Converts timestamp range to Dayjs date range for UI components.
 * Uses nullish checks (== null) to handle timestamp 0 correctly.
 */
export const toDayjsRange = ({ fromTimestamp, toTimestamp }: TimestampRange): [Dayjs | null, Dayjs | null] => {
  return [fromTimestamp == null ? null : dayjs(fromTimestamp), toTimestamp == null ? null : dayjs(toTimestamp)];
};

/**
 * Converts Dayjs date range from UI components to timestamp range for APIs.
 * Returns null if either date is missing.
 */
export const toTimestampRange = ([fromDate, toDate]: [Dayjs | null, Dayjs | null]): TimestampRange | null => {
  if (!fromDate || !toDate) return null;

  return {
    fromTimestamp: fromDate.startOf('day').valueOf(),
    toTimestamp: toDate.endOf('day').valueOf(),
  };
};
